import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import { FullscreenModal, LargeSwitch } from "shared";
import {
  useStoreInvestmentTransaction,
  useUpdateInvestmentTransaction,
  useUploadInvestmentFile,
  useGetInvestmentTransactions,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import TransactionForm from "./TransactionForm";
import { TransactionFormValues, TransactionType } from "./forms";
import { InvestmentTransactionType } from "hooks/investments/GetInvestmentTransactionsHook";
import "./AddInvestmentTransactionModal.scss";

const TRANSACTION_TYPE_OPTIONS = [
  { value: "sell", label: "Sprzedaż" },
  { value: "buy", label: "Kupno" },
];

interface AddInvestmentTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  item: any | null;
  investmentType: any;
  defaultType?: TransactionType;
  isOwn?: boolean;
  currentQuantity?: number;
  editTransaction?: InvestmentTransactionType | null;
}

const AddInvestmentTransactionModal = ({
  isOpen,
  onClose,
  onSuccess,
  item,
  investmentType,
  defaultType = "sell",
  isOwn = false,
  currentQuantity,
  editTransaction = null,
}: AddInvestmentTransactionModalProps) => {
  const slug = investmentType?.slug ?? "";
  const [transactionType, setTransactionType] =
    useState<TransactionType>(defaultType);

  useEffect(() => {
    if (isOpen) setTransactionType(defaultType);
  }, [isOpen, defaultType]);
  // Pobierz transakcje gdy currentQuantity nie pochodzi z SideModal
  const needsFetch =
    (slug === "treasury_bonds" || slug === "currencies" || slug === "crypto") &&
    currentQuantity === undefined;
  const { data: txData } = useGetInvestmentTransactions(
    needsFetch && isOpen ? item?.uuid ?? null : null,
  );
  const fetchedCurrentQuantity: number | undefined = txData
    ? slug === "currencies"
      ? // Dla walut używaj totalValue zamiast quantity
        txData.transactions
          .filter((tx: InvestmentTransactionType) => tx.type === "buy")
          .reduce(
            (sum: number, tx: InvestmentTransactionType) =>
              sum + (tx.totalValue ?? 0),
            0,
          ) -
        txData.transactions
          .filter((tx: InvestmentTransactionType) => tx.type === "sell")
          .reduce(
            (sum: number, tx: InvestmentTransactionType) =>
              sum + (tx.totalValue ?? 0),
            0,
          )
      : // Dla innych aktywów używaj quantity
        txData.transactions
          .filter((tx: InvestmentTransactionType) => tx.type === "buy")
          .reduce(
            (sum: number, tx: InvestmentTransactionType) =>
              sum + (tx.quantity ?? 0),
            0,
          ) -
        txData.transactions
          .filter((tx: InvestmentTransactionType) => tx.type === "sell")
          .reduce(
            (sum: number, tx: InvestmentTransactionType) =>
              sum + (tx.quantity ?? 0),
            0,
          )
    : undefined;
  const resolvedCurrentQuantity = currentQuantity ?? fetchedCurrentQuantity;

  const { mutateAsync: storeTransaction, isPending: isStorePending } =
    useStoreInvestmentTransaction();
  const { mutateAsync: updateTransaction, isPending: isUpdatePending } =
    useUpdateInvestmentTransaction();
  const isPending = isStorePending || isUpdatePending;
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { data: me } = useGetMe();

  const handleSubmit = async (values: TransactionFormValues) => {
    if (!item?.uuid) return;

    if (
      activeType === "sell" &&
      (slug === "treasury_bonds" || slug === "crypto") &&
      effectiveMaxQuantity !== undefined &&
      Number(values.quantity) > effectiveMaxQuantity
    ) {
      return;
    }

    const { file, ...rest } = values;

    let fileId: number | null = null;
    if (file instanceof File) {
      try {
        const userId: string | number =
          (me as any)?.user?.id ?? (me as any)?.id ?? "";
        const uploaded = await uploadFile({
          file,
          type: "attachment",
          parentId: userId,
        });
        fileId = Array.isArray(uploaded)
          ? uploaded[0]?.id ?? null
          : (uploaded as any)?.id ?? null;
      } catch {
        // błąd pliku nie blokuje transakcji
      }
    }

    const isCrypto = slug === "crypto";

    const payload = {
      type: isOwn ? transactionType : defaultType,
      quantity: rest.quantity ? Number(rest.quantity) : null,
      unitPrice: isCrypto
        ? null
        : rest.unitPrice
        ? Number(rest.unitPrice)
        : null,
      totalValue: isCrypto
        ? rest.unitPrice
          ? Number(rest.unitPrice)
          : 0
        : Number(rest.totalValue),
      currencyUuid: rest.currencyUuid || item.currencyUuid,
      note: rest.note || null,
      completedAt: rest.completedAt || null,
      fileId,
    };

    if (editTransaction) {
      await updateTransaction(
        {
          investmentUuid: item.uuid,
          transactionUuid: editTransaction.uuid,
          ...payload,
        },
        { onSuccess: () => onClose() },
      );
    } else {
      await storeTransaction(
        { investmentUuid: item.uuid, data: payload },
        {
          onSuccess: () => {
            onClose();
            onSuccess?.();
          },
        },
      );
    }
  };

  if (!item || !slug) return null;

  const titleMap: Record<string, Record<string, string>> = {
    treasury_bonds: {
      sell: "Sprzedaż Obligacji Skarbowych",
      buy: "Zakup Obligacji Skarbowych",
    },
    precious_stones: {
      sell: "Sprzedaż Kamieni Szlachetnych",
      buy: "Zakup Kamieni Szlachetnych",
    },
    precious_metals: {
      sell: "Sprzedaż Metali Szlachetnych",
      buy: "Zakup Metali Szlachetnych",
    },
    currencies: {
      sell: "Sprzedaż Walut",
      buy: "Zakup Walut",
    },
  };

  console.log(investmentType, "investmentType");
  const isEditMode = !!editTransaction;
  const activeType = isOwn && !isEditMode ? transactionType : defaultType;
  const typeLabel = activeType === "sell" ? "Sprzedaż" : "Zakup";
  const title =
    isOwn && !isEditMode
      ? "Zaznacz sprzedaż / kupno"
      : titleMap[slug]?.[activeType] ??
        `${typeLabel} ${investmentType?.name ?? ""}`;

  const editInitialValues: Partial<TransactionFormValues> | undefined =
    editTransaction
      ? {
          type: editTransaction.type,
          quantity:
            editTransaction.quantity != null
              ? String(editTransaction.quantity)
              : "",
          unitPrice:
            editTransaction.unitPrice != null
              ? String(editTransaction.unitPrice)
              : "",
          totalValue:
            editTransaction.totalValue != null
              ? String(editTransaction.totalValue)
              : "",
          currencyUuid: editTransaction.currencyUuid ?? "",
          note: editTransaction.note ?? "",
          completedAt: editTransaction.completedAt
            ? editTransaction.completedAt.split("T")[0]
            : "",
          file: null,
        }
      : undefined;

  const currencyLabel = item?.currency?.currency ?? "";

  const effectiveMaxQuantity =
    resolvedCurrentQuantity !== undefined && editTransaction?.type === "sell"
      ? resolvedCurrentQuantity + (editTransaction.quantity ?? 0)
      : resolvedCurrentQuantity;

  const showSellWarning =
    (slug === "treasury_bonds" || slug === "currencies" || slug === "crypto") &&
    activeType === "sell" &&
    effectiveMaxQuantity !== undefined;

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="add-investment-transaction-modal">
        <h2 className="fullscreen-modal__header">{title}</h2>
        {isOwn && !isEditMode && (
          <LargeSwitch
            className="add-investment-transaction-modal__type-switch"
            state={transactionType}
            options={TRANSACTION_TYPE_OPTIONS}
            onChange={(val) => setTransactionType(val as TransactionType)}
          />
        )}
        {/* <p className="add-investment-transaction-modal__subtitle">
          <b>Aktywo:</b> {item?.name ?? "—"}
        </p> */}
        <TransactionForm
          slug={slug}
          defaultType={activeType}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isLoading={isPending}
          submitLabel={isEditMode ? "Zapisz zmiany" : "Potwierdź"}
          maxQuantity={showSellWarning ? effectiveMaxQuantity : undefined}
          warningText={
            showSellWarning
              ? slug === "currencies"
                ? `Nie możesz sprzedać więcej walut niż posiadasz. Obecnie posiadasz ${effectiveMaxQuantity}${
                    currencyLabel ? ` ${currencyLabel}` : ""
                  }.`
                : slug === "crypto"
                ? `Nie możesz sprzedać więcej kryptowalut niż posiadasz. Obecnie posiadasz ${effectiveMaxQuantity}.`
                : `Nie możesz sprzedać więcej obligacji skarbowych niż posiadasz. Obecnie posiadasz ${effectiveMaxQuantity}`
              : undefined
          }
          initialValues={editInitialValues}
          currencyCode={item?.currency?.currency ?? undefined}
          existingFile={editTransaction?.file ?? null}
        />
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default AddInvestmentTransactionModal;
